import React, { useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Grid2,
  Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { Appointment, BookingConfirmation, Employee, Service } from '../../types';
import { dayjs } from '../../utils/dayjs-config';
import { RecaptchaProvider } from '../../utils/ReCaptchaContext';
import ErrorDialog from '../global/ErrorDialog';
import LoadingSpinner from '../global/LoadingSpinner';
import BookingConfirmationDetails from './BookingConfirmationDetails';
import ConfirmAppointment from './ConfirmAppointment';
import DatePicker from './DatePicker';
import EmployeePicker from './EmployeePicker';
import InternalConfirmAppointment from './InternalConfirmAppointment';
import ServicePicker from './ServicePicker';
import TimeSlotPicker from './TimeSlotPicker';
import { useBookNowViewModel } from './useBookNowViewModel';

interface BookNowDialogProps {
  appointment: Appointment | null;
  handleAppointment: (appointment: Appointment | null) => void;
  services: Service[];
  handleCloseBooking: () => void;
  enabledCustomerSMS: boolean;
  enabledEmployeeSMS: boolean;
  enabledPayments: boolean;
  requirePaymentForBooking: boolean;
  minimumBookingNoticeHours: number;
  variant?: 'customer' | 'internal';
  allowedEmployeeIds?: string[];
  preferredEmployeeId?: string;
  onBookingSuccess?: (booking: BookingConfirmation) => void;
  clearAppointmentOnSuccess?: boolean;
  scheduleCacheOverride?: Map<string, string> | null;
  scheduleCacheOverrideEmployeeId?: string;
  prefillCustomer?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null;
  initialStep?: number;
  employeesOverride?: Employee[];
}

const BookNowDialog: React.FC<BookNowDialogProps> = ({
  appointment,
  handleAppointment,
  services,
  handleCloseBooking,
  enabledCustomerSMS,
  enabledEmployeeSMS,
  enabledPayments,
  requirePaymentForBooking,
  minimumBookingNoticeHours,
  variant = 'customer',
  allowedEmployeeIds,
  preferredEmployeeId,
  onBookingSuccess,
  clearAppointmentOnSuccess,
  scheduleCacheOverride,
  scheduleCacheOverrideEmployeeId,
  prefillCustomer,
  initialStep,
  employeesOverride,
}) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const vm = useBookNowViewModel({
    appointment,
    handleAppointment,
    services,
    enabledPayments,
    requirePaymentForBooking,
    minimumBookingNoticeHours,
    variant,
    allowedEmployeeIds,
    preferredEmployeeId,
    onBookingSuccess,
    clearAppointmentOnSuccess,
    scheduleCacheOverride,
    scheduleCacheOverrideEmployeeId,
    initialStep,
    employeesOverride,
  });
  const configuredSelectResourceLabel = vm.businessConfig?.preferences?.terminology?.selectResourceLabel?.trim();
  const selectResourceLabel = configuredSelectResourceLabel || 'Select employee';
  const configuredAnyAvailableLabel = vm.businessConfig?.preferences?.terminology?.anyAvailableLabel?.trim();
  const anyAvailableLabel = configuredAnyAvailableLabel || 'Any Available Staff';

  const renderStepContent = () => {
    switch (vm.currentStep) {
      case 0:
        if (vm.isOrchestratorMode || vm.shouldHideEmployeePicker) {
          return null;
        }
        return (
          <>
            {(vm.availableEmployees.length > 0) && vm.imagesLoaded && (
              <EmployeePicker
                employeeSelected={vm.isAnyAvailableSelected ? null : vm.selectedEmployee}
                isAnyAvailableSelected={vm.isAnyAvailableSelected}
                allowAnyAvailable={vm.allowAnyAvailable}
                employees={vm.availableEmployees}
                setSelectedEmployee={vm.setSelectedEmployee}
                setIsAnyAvailableSelected={vm.setIsAnyAvailableSelected}
                setSelectedServicesList={vm.setSelectedServicesList}
                profilePictures={vm.profilePictures}
                goToNextStep={vm.goToNextStep}
                currentAppointment={appointment}
                handleAppointment={handleAppointment}
                selectLabel={selectResourceLabel}
                anyAvailableLabel={anyAvailableLabel}
              />
            )}
          </>
        );
      case 1:
        return (
          <>
            {(services && (vm.selectedEmployee || vm.isAnyAvailableSelected)) &&
              <ServicePicker
                services={services}
                handleAppointment={handleAppointment}
                selectedEmployee={vm.selectedEmployee}
                eligibleEmployees={vm.bookableEligibleEmployees}
                isAnyAvailableSelected={vm.isAnyAvailableSelected}
                isAnyAvailableBlockedByPaymentCompatibility={vm.isAnyAvailableBlockedByPaymentCompatibility}
                selectedServicesList={vm.selectedServicesList}
                setSelectedServicesList={vm.setSelectedServicesList}
                currentAppointment={appointment}
                resetDateTimeOnServiceChange={variant !== 'internal'}
              />
            }
          </>
        );
      case 2:
        return (
          <>
            {(vm.scheduleCacheForSelection && appointment) && (
              <DatePicker
                appointment={appointment}
                handleAppointment={handleAppointment}
                updateAppointment={true}
                initialDate={appointment.appointmentDate ? dayjs(appointment.appointmentDate, 'MMDDYYYY') : dayjs.tz()}
                appointmentDuration={appointment.appointmentDuration}
                scheduleCache={vm.scheduleCacheForSelection}
                availableSlotsByDate={vm.anyAvailableSlotsByDate ?? undefined}
                disableDates={true}
                setAvailableSlots={vm.setAvailableSlots}
                goToNextStep={vm.goToNextStep}
                minimumBookingNoticeHours={vm.normalizedMinimumBookingNoticeHours}
                allowPastDates={variant === 'internal'}
              />
            )}
          </>
        );
      case 3:
        return (
          <>
            {(appointment && vm.availableSlots) && (
              <TimeSlotPicker
                appointment={appointment}
                handleAppointment={handleAppointment}
                updateAppointment={true}
                availableSlots={vm.availableSlots}
                onSlotSelect={vm.isAnyAvailableSelected ? vm.handleAnyAvailableSlotSelect : undefined}
                goToNextStep={vm.goToNextStep}
                navigateToNextStep={true}
              />
            )}
          </>
        );
      case 4:
        if (!(vm.selectedServicesList && appointment && appointment.appointmentDate && appointment.appointmentTime && vm.selectedEmployee)) {
          return null;
        }

        if (variant === 'internal') {
          return (
            <InternalConfirmAppointment
              employee={vm.selectedEmployee}
              appointment={appointment}
              businessName={vm.businessConfig?.businessInfo.name || ''}
              enabledEmployeeSMS={enabledEmployeeSMS}
              handleAppointment={handleAppointment}
              setIsSubmitting={vm.setIsSubmitting}
              handleErrorDialogOpen={vm.handleErrorDialogOpen}
              handleScheduleCacheUpdate={vm.handleScheduleCacheUpdate}
              handleBookingConfirmation={vm.handleBookingConfirmation}
              goToPreviousStep={vm.goToPreviousStep}
              clearAppointmentOnSuccess={vm.shouldClearAfterSuccess}
            />
          );
        }

        return (
          <ConfirmAppointment
            employee={vm.selectedEmployee}
            appointment={appointment}
            businessName={vm.businessConfig?.businessInfo.name || ''}
            businessConfig={vm.businessConfig ?? null}
            allEmployees={vm.allEmployees}
            handleAppointment={handleAppointment}
            setIsSubmitting={vm.setIsSubmitting}
            handleErrorDialogOpen={vm.handleErrorDialogOpen}
            handleScheduleCacheUpdate={vm.handleScheduleCacheUpdate}
            handleBookingConfirmation={vm.handleBookingConfirmation}
            enabledCustomerSMS={enabledCustomerSMS}
            enabledEmployeeSMS={enabledEmployeeSMS}
            goToPreviousStep={vm.goToPreviousStep}
            canTakeCard={vm.canTakeCard}
            enabledPayments={enabledPayments}
            requirePaymentForBooking={requirePaymentForBooking}
            allowTips={vm.businessConfig?.preferences?.allowTips ?? false}
            clearAppointmentOnSuccess={vm.shouldClearAfterSuccess}
            prefillCustomer={prefillCustomer ?? undefined}
            resolveAnyAvailableConflict={vm.handleAnyAvailableConflictResolution}
          />
        );
      default:
        return null;
    }
  };

  const dialogLayout = (
    <Grid2 container spacing={2} direction="column" alignItems="center" sx={{ height: '100%' }} >
      <ErrorDialog
        open={vm.openErrorDialog}
        onClose={vm.handleErrorDialogClose}
        message={vm.errorMessage}
        onExited={vm.handleErrorDialogExited}
      />

      {vm.isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Container
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              height: '100%',
              position: 'relative',
            }}
          >
            {(vm.bookingConfirmation) ? (
              <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 3 }}>
                <BookingConfirmationDetails
                  bookingConfirmation={vm.bookingConfirmation}
                  paid={vm.paid}
                  attemptedCardPayment={vm.attemptedCardPayment}
                  isInternal={variant === 'internal'}
                />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { width: 0, height: 0 },
                    boxShadow: 'none',
                    pt: 3,
                    pb: 0,
                  }}
                >
                  <Typography variant="h6" align="center" fontWeight={500} gutterBottom>
                    Book Now
                  </Typography>
                  {((!vm.isOrchestratorMode && vm.selectionSummary.employeeLabel) || vm.selectionSummary.serviceTitles) && vm.currentStep !== vm.steps.length - 1 && (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      {!vm.isOrchestratorMode && !vm.shouldHideEmployeePicker && vm.selectionSummary.employeeLabel && (
                        <Typography variant="body2" color="text.secondary">
                          With {vm.selectionSummary.employeeLabel}
                        </Typography>
                      )}
                      {vm.selectionSummary.serviceTitles && (
                        <Typography variant="body2" color="text.secondary">
                          Services: {vm.selectionSummary.serviceTitles}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <hr
                    style={{
                      border: 'none',
                      height: '2px',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                      boxShadow: '0 1px 5px rgba(0, 0, 0, 0.3)',
                      marginTop: '12px',
                      marginBottom: '12px',
                      width: '75%'
                    }}
                  />
                  {renderStepContent()}
                </Box>

                {vm.currentStep < vm.steps.length - 1 && (
                  <Box
                    sx={{
                      position: 'sticky',
                      bottom: 0,
                      width: '100%',
                      py: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'background.paper',
                      zIndex: 2,
                    }}
                  >
                    <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center', gap: 2 }}>
                      {vm.currentStep === 0 && (
                        <Button
                          variant="text"
                          onClick={handleCloseBooking}
                          sx={{ width: '100%', borderRadius: '4px', fontWeight: '600', fontSize: '1rem', py: 1.2 }}
                        >
                          Close
                        </Button>
                      )}

                      {vm.currentStep > (vm.isOrchestratorMode ? 1 : 0) && vm.currentStep < vm.steps.length - 1 && (
                        <Button
                          variant="text"
                          onClick={vm.goToPreviousStep}
                          disabled={vm.currentStep <= (vm.isOrchestratorMode ? 1 : 0)}
                          sx={{ width: '100%', borderRadius: '4px', fontWeight: '600', fontSize: '1rem', py: 1.2 }}
                        >
                          Back
                        </Button>
                      )}
                    </Box>

                    <Box sx={{ width: '50%', display: 'flex', justifyContent: 'center' }}>
                      {vm.currentStep < vm.steps.length - 1 && (
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={vm.handleNextStep}
                          disabled={vm.isNextDisabled}
                          sx={{
                            width: '100%',
                            borderRadius: '4px',
                            fontWeight: '600',
                            fontSize: '1rem',
                            py: 1.2,
                            color: 'common.white',
                            '&:hover': {
                              color: 'common.white',
                            },
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Container>
        </>
      )}
    </Grid2>
  );

  if (variant === 'internal') {
    return dialogLayout;
  }

  return (
    <RecaptchaProvider recaptchaRef={recaptchaRef}>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
        size="invisible"
        badge="bottomright"
      />
      {dialogLayout}
    </RecaptchaProvider>
  );
};

export default BookNowDialog;
